import type * as React from "react";

import { faArrowRightFromBracket, faGift } from "@performyard/fontawesome/pro-light-svg-icons";
import type { SideNavUserMenuOption } from "@performyard/py-components";

import type { NavigationConfig } from "../../api/services/defs/navigation";

import { resetPostHog } from "./posthogReset";

type CelloCommandApi = {
	open?: () => unknown;
};

type CelloCommandCallback = (cello: CelloCommandApi) => void;

declare global {
	interface Window {
		Cello?: (method: string, ...args: unknown[]) => unknown;
		cello?: { cmd?: { push: (callback: CelloCommandCallback) => unknown } | CelloCommandCallback[] };
		openCelloReferral?: () => void;
	}
}

const openBeacon = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
	event.preventDefault();
	if (typeof window !== "undefined" && window.Beacon) {
		window.Beacon("open");
	}
};

const openCello = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
	event.preventDefault();
	if (typeof window === "undefined") return;

	if (typeof window.openCelloReferral === "function") {
		window.openCelloReferral();
		return;
	}

	if (typeof window.Cello === "function") {
		window.Cello("open");
		return;
	}

	if (window.cello?.cmd) {
		window.cello.cmd.push((cello) => {
			if (typeof cello.open === "function") {
				cello.open();
				return;
			}

			window.Cello?.("open");
		});
		return;
	}

	console.warn("Cello referral SDK is unavailable; cannot open the referral widget.");
};

type UserMenuOptionsExtras = {
	isInAppReferralsEnabled?: boolean;
};

/** Single source of truth for the SideNav footer + TopBar avatar menus. */
export const buildUserMenuOptions = (
	config: NavigationConfig,
	{ isInAppReferralsEnabled = false }: UserMenuOptionsExtras = {}
): SideNavUserMenuOption[] => {
	const options: SideNavUserMenuOption[] = [];
	if (config.possessor) {
		options.push({
			id: "unpossess",
			label: `Return to ${config.possessor.name}`,
			href: "/unpossess/",
			onClick: () => resetPostHog(),
		});
	}
	if (config.isOverlord) {
		options.push({ id: "super-user", label: "Super User", href: "/super-user/" });
	}
	options.push({
		id: "employee-details",
		label: "Employee Details",
		href: `/details/${config.currentUserId}/`,
		className: "no-tooltip",
	});
	options.push({
		id: "settings",
		label: "Settings",
		href: config.isEngagementOnly ? "/settings/account/" : "/settings/notifications/",
	});
	options.push({ id: "help", label: "Get Help", onClick: openBeacon });
	options.push({
		id: "support",
		label: "Support",
		href: "http://support.performyard.com",
		isExternal: true,
	});
	if (isInAppReferralsEnabled) {
		options.push({
			id: "refer-and-earn",
			label: "Refer & Earn",
			onClick: openCello,
			icon: faGift,
			"data-testid": "refer-and-earn-menu-item",
		});
	}
	options.push({
		id: "logout",
		label: `Logout, ${config.currentUserName}`,
		href: "/logout/",
		// FA 6.7 has no arrow-left-from-bracket; mirror the right-facing one.
		icon: faArrowRightFromBracket,
		iconClassName: "-scale-x-100",
		divider: "before",
	});
	return options;
};
